import { useMutation, useQuery } from "@apollo/client";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import Errors from "components/base/Errors";
import Button from "components/base/Button";
import { Input } from "components/base/Form";
import SearchableSelect from "components/base/SearchableSelect";
import { useState, useMemo, useEffect } from "react";
import { useModals } from "ModalProvider";
import { Alert } from "components/base/Toast";
import { BsPencil, BsTrash, BsSearch } from "react-icons/bs";
import { FaAsterisk } from "react-icons/fa6";
import {
  FETCH_ALL_PACKAGE_ADDRESSES,
  CREATE_PACKAGE_ADDRESS,
  UPDATE_PACKAGE_ADDRESS,
  DELETE_PACKAGE_ADDRESS,
  FETCH_ALL_PACKAGE_COUNTRIES,
} from "./graphql";

const formatCountryOptionName = (displayName, code) => {
  return code ? `${displayName} (${code})` : displayName;
};

const getCountryDisplayName = (countryCode, countryName = "") => {
  return countryName || countryCode;
};

const normalizeCountryValue = (value = "") => value.trim().toLowerCase();

const findCountryOption = (countryOptions, countryValue, countryNameValue) => {
  const candidates = [countryValue, countryNameValue]
    .map((value) => normalizeCountryValue(value || ""))
    .filter(Boolean);

  if (!candidates.length) return null;

  return countryOptions.find((option) => {
    const optionValues = [option.code, option.displayName, ...option.names].map(
      normalizeCountryValue,
    );
    return candidates.some((candidate) => optionValues.includes(candidate));
  });
};

const parseCountryOptions = (countries) => {
  return (countries || [])
    .map((country) => {
      const countryNames = Array.isArray(country.name)
        ? country.name
        : [country.name];
      const displayName = countryNames.find((name) => name);
      return {
        code: country.code,
        displayName,
        names: countryNames,
        searchText: `${countryNames.join(" ")} ${country.code}`.toLowerCase(),
      };
    })
    .filter((country) => country.code && country.displayName)
    .map((country) => ({
      code: country.code,
      value: country.code,
      label: formatCountryOptionName(country.displayName, country.code),
      displayName: country.displayName,
      names: country.names,
      name: formatCountryOptionName(country.displayName, country.code),
      searchText: country.searchText,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
};

const FIELD_LENGTH_LIMITS = {
  shortName: 15,
  company: 50,
  attention: 50,
  email: 254,
  phone: 20,
  streetAddress: 100,
  streetAddress2: 100,
  cityPostal: 50,
};

const getLengthError = (fields) => {
  const overLimitField = fields.find(
    ({ value, maxLength }) => value.length > maxLength,
  );

  if (!overLimitField) return null;

  return `${overLimitField.label} is too long. Max ${overLimitField.maxLength} characters, currently ${overLimitField.value.length}.`;
};

const isOverLimit = (value, maxLength) => value.length > maxLength;

const PackageAddressForm = ({
  hide,
  address,
  action,
  existingAddresses = [],
}) => {
  const addressId = address ? address.id : "";
  const [shortName, setShortName] = useState(address ? address.shortName : "");
  const [company, setCompany] = useState(address ? address.company : "");
  const [attention, setAttention] = useState(address ? address.attention : "");
  const [email, setEmail] = useState(address ? address.email : "");
  const [phone, setPhone] = useState(address ? address.phone : "");
  const [streetAddress, setStreetAddress] = useState(
    address ? address.streetAddress : "",
  );
  const [streetAddress2, setStreetAddress2] = useState(
    address ? address.streetAddress2 : "",
  );
  const [cityPostal, setCityPostal] = useState(
    address ? address.cityPostal : "",
  );
  const [country, setCountry] = useState(address ? address.country : "");
  const [countryName, setCountryName] = useState(
    address ? getCountryDisplayName(address.country, address.countryName) : "",
  );
  const [isShortNameCustomized, setIsShortNameCustomized] = useState(false);

  const { data: countryData } = useQuery(FETCH_ALL_PACKAGE_COUNTRIES, {
    skip: action === "delete",
  });

  const countryOptions = useMemo(() => {
    return parseCountryOptions(countryData?.allPackageCountries);
  }, [countryData]);

  useEffect(() => {
    if (action === "delete" || !countryOptions.length) return;

    const countryOption = findCountryOption(
      countryOptions,
      country,
      countryName,
    );
    if (!countryOption) return;

    if (country !== countryOption.code) {
      setCountry(countryOption.code);
    }
    if (countryName !== countryOption.displayName) {
      setCountryName(countryOption.displayName);
    }
  }, [action, country, countryName, countryOptions]);

  const handleCountrySelect = (option) => {
    setCountry(option.code);
    setCountryName(option.displayName);
    setIsShortNameCustomized(false);
    updateShortName(option.code, company, attention);
  };

  const handleCompanyChange = (e) => {
    const newValue = e.target.value;
    setCompany(newValue);
    setIsShortNameCustomized(false);
    updateShortName(country, newValue, attention);
  };

  const handleAttentionChange = (e) => {
    const newValue = e.target.value;
    setAttention(newValue);
    setIsShortNameCustomized(false);
    updateShortName(country, company, newValue);
  };

  const handleShortNameChange = (e) => {
    const newValue = e.target.value;
    setShortName(newValue);
    if (newValue.trim() !== "") {
      setIsShortNameCustomized(true);
    } else {
      setIsShortNameCustomized(false);
      updateShortName(country, company, attention);
    }
  };

  const updateShortName = (countryCode, companyValue, attentionValue) => {
    const trimmedCountryCode = countryCode.trim();
    const trimmedAttention = attentionValue.trim();

    if (!trimmedCountryCode || !trimmedAttention) {
      setShortName("");
      return;
    }

    const prefix = companyValue.trim() ? "B" : "C";
    const attentionInitials = trimmedAttention
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase())
      .join("");

    const generatedShortName = `${prefix}-${trimmedCountryCode}-${attentionInitials}`;
    setShortName(generatedShortName.substring(0, 15));
  };

  useEffect(() => {
    if (
      (action === "create" || !address?.shortName) &&
      !isShortNameCustomized
    ) {
      updateShortName(country, company, attention);
    }
  }, [action, address]);

  const [createPackageAddress] = useMutation(CREATE_PACKAGE_ADDRESS, {
    refetchQueries: [{ query: FETCH_ALL_PACKAGE_ADDRESSES }],
    onCompleted: () => {
      hide();
      Alert("success", "Package address has been created.");
    },
  });

  const [updatePackageAddress] = useMutation(UPDATE_PACKAGE_ADDRESS, {
    refetchQueries: [{ query: FETCH_ALL_PACKAGE_ADDRESSES }],
    onCompleted: () => {
      hide();
      Alert("success", "Package address has been updated.");
    },
  });

  const [deletePackageAddress] = useMutation(DELETE_PACKAGE_ADDRESS, {
    variables: { id: addressId },
    refetchQueries: [{ query: FETCH_ALL_PACKAGE_ADDRESSES }],
    onCompleted: () => {
      hide();
      Alert("success", "Package address has been deleted.");
    },
  });

  const handleSubmit = () => {
    const trimmedShortName = shortName.trim();
    const trimmedCompany = company.trim();
    const trimmedAttention = attention.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    const trimmedStreetAddress = streetAddress.trim();
    const trimmedStreetAddress2 = streetAddress2.trim();
    const trimmedCityPostal = cityPostal.trim();
    const trimmedCountry = country.trim();
    const trimmedCountryName = countryName.trim();

    if (
      !trimmedShortName ||
      !trimmedStreetAddress ||
      !trimmedCityPostal ||
      !trimmedCountry ||
      !trimmedCountryName ||
      !trimmedPhone
    ) {
      Alert("error", "Please fill in all required fields.");
      return;
    }

    const lengthError = getLengthError([
      {
        label: "Street Address",
        value: trimmedStreetAddress,
        maxLength: FIELD_LENGTH_LIMITS.streetAddress,
      },
      {
        label: "Street Address 2",
        value: trimmedStreetAddress2,
        maxLength: FIELD_LENGTH_LIMITS.streetAddress2,
      },
      {
        label: "City & Postal",
        value: trimmedCityPostal,
        maxLength: FIELD_LENGTH_LIMITS.cityPostal,
      },
      {
        label: "Company",
        value: trimmedCompany,
        maxLength: FIELD_LENGTH_LIMITS.company,
      },
      {
        label: "Attention",
        value: trimmedAttention,
        maxLength: FIELD_LENGTH_LIMITS.attention,
      },
      {
        label: "Email",
        value: trimmedEmail,
        maxLength: FIELD_LENGTH_LIMITS.email,
      },
      {
        label: "Phone",
        value: trimmedPhone,
        maxLength: FIELD_LENGTH_LIMITS.phone,
      },
      {
        label: "Short Name",
        value: trimmedShortName,
        maxLength: FIELD_LENGTH_LIMITS.shortName,
      },
    ]);

    if (lengthError) {
      Alert("error", lengthError);
      return;
    }

    const isDuplicateShortName = existingAddresses.some((existingAddress) => {
      if (addressId && existingAddress.id === addressId) {
        return false;
      }
      return (
        existingAddress.shortName?.toLowerCase() ===
        trimmedShortName.toLowerCase()
      );
    });

    if (isDuplicateShortName) {
      Alert(
        "error",
        `Short name "${trimmedShortName}" already exists. Please use a different name.`,
      );
      return;
    }

    if (addressId) {
      updatePackageAddress({
        variables: {
          id: addressId,
          shortName: trimmedShortName,
          company: trimmedCompany,
          attention: trimmedAttention,
          email: trimmedEmail,
          phone: trimmedPhone,
          streetAddress: trimmedStreetAddress,
          streetAddress2: trimmedStreetAddress2,
          cityPostal: trimmedCityPostal,
          country: trimmedCountry,
          countryName: trimmedCountryName,
        },
      });
    } else {
      createPackageAddress({
        variables: {
          shortName: trimmedShortName,
          company: trimmedCompany,
          attention: trimmedAttention,
          email: trimmedEmail,
          phone: trimmedPhone,
          streetAddress: trimmedStreetAddress,
          streetAddress2: trimmedStreetAddress2,
          cityPostal: trimmedCityPostal,
          country: trimmedCountry,
          countryName: trimmedCountryName,
        },
      });
    }
  };

  return (
    <div data-testid="package-address-form">
      {action === "delete" ? (
        <>
          <p className="opacity-80 mb-4">Delete this destination address?</p>
          <div className="text-sm space-y-2 card bg-gray-100">
            <div className="flex">
              <label className="w-40">Name: </label>
              {address.shortName}
            </div>
            <div className="flex">
              <label className="w-40">Address: </label>
              <div className="flex flex-col">
                {address.streetAddress && <span>{address.streetAddress}</span>}
                {address.streetAddress2 && (
                  <span>{address.streetAddress2}</span>
                )}
                {address.cityPostal && <span>{address.cityPostal}</span>}
                {address.country && (
                  <span>
                    {getCountryDisplayName(
                      address.country,
                      address.countryName,
                    )}
                  </span>
                )}
              </div>
            </div>
            <div className="flex">
              <label className="w-40">Company: </label>
              {address.company}
            </div>
            <div className="flex">
              <label className="w-40">Attention: </label>
              {address.attention}
            </div>
            <div className="flex">
              <label className="w-40">Email: </label>
              {address.email}
            </div>
            <div className="flex">
              <label className="w-40">Phone: </label>
              {address.phone}
            </div>
          </div>

          <div className="flex space-x-4 mt-4">
            <Button onClick={deletePackageAddress} danger={true}>
              Delete
            </Button>
            <Button
              onClick={hide}
              className="text-gray-500 hover:text-gray-800"
            >
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="text-sm space-y-2 card bg-gray-100">
            <div className="flex items-center">
              <label className="w-40">Street Address: </label>
              <Input
                type="text"
                value={streetAddress}
                onChange={(e) => setStreetAddress(e.target.value)}
                placeholder="Street address"
                className="w-[28rem]"
                validated={
                  !isOverLimit(streetAddress, FIELD_LENGTH_LIMITS.streetAddress)
                }
              />
              <span
                className={`ml-2 text-xs ${
                  isOverLimit(streetAddress, FIELD_LENGTH_LIMITS.streetAddress)
                    ? "text-red-500"
                    : "text-gray-400"
                }`}
              >
                {streetAddress.length}/{FIELD_LENGTH_LIMITS.streetAddress}
              </span>
              <FaAsterisk className="text-red-500 text-xxs ml-2" />
            </div>
            <div className="flex items-center">
              <label className="w-40">Street Address 2: </label>
              <Input
                type="text"
                value={streetAddress2}
                onChange={(e) => setStreetAddress2(e.target.value)}
                placeholder="Apartment, suite, unit, building, floor"
                className="w-[28rem]"
                validated={
                  !isOverLimit(
                    streetAddress2,
                    FIELD_LENGTH_LIMITS.streetAddress2,
                  )
                }
              />
              <span
                className={`ml-2 text-xs ${
                  isOverLimit(
                    streetAddress2,
                    FIELD_LENGTH_LIMITS.streetAddress2,
                  )
                    ? "text-red-500"
                    : "text-gray-400"
                }`}
              >
                {streetAddress2.length}/{FIELD_LENGTH_LIMITS.streetAddress2}
              </span>
            </div>
            <div className="flex items-center">
              <label className="w-40">City & Postal: </label>
              <Input
                type="text"
                value={cityPostal}
                onChange={(e) => setCityPostal(e.target.value)}
                placeholder="City and postal code"
                className="w-[28rem]"
                maxLength={FIELD_LENGTH_LIMITS.cityPostal}
              />
              <FaAsterisk className="text-red-500 text-xxs ml-2" />
            </div>
            <div className="flex items-center">
              <label className="w-40">Country: </label>
              <SearchableSelect
                className="w-[28rem]"
                options={countryOptions}
                value={country}
                onSelect={handleCountrySelect}
                placeholder="Select a country"
                searchPlaceholder="Search country"
                emptyText="No countries found"
                renderOption={(option) => option.name}
                renderValue={(option) => option.name}
                searchFields={["label", "displayName", "code", "searchText"]}
              />
              <FaAsterisk className="text-red-500 text-xxs ml-2" />
            </div>
            <div className="flex items-center">
              <label className="w-40">Company: </label>
              <Input
                type="text"
                value={company}
                onChange={handleCompanyChange}
                placeholder="Company name"
                className="w-80"
                maxLength={FIELD_LENGTH_LIMITS.company}
              />
            </div>
            <div className="flex items-center">
              <label className="w-40">Attention: </label>
              <Input
                type="text"
                value={attention}
                onChange={handleAttentionChange}
                placeholder="Contact person"
                className="w-80"
                maxLength={FIELD_LENGTH_LIMITS.attention}
              />
            </div>
            <div className="flex items-center">
              <label className="w-40">Email: </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Contact email address"
                className="w-80"
                maxLength={FIELD_LENGTH_LIMITS.email}
              />
            </div>
            <div className="flex items-center">
              <label className="w-40">Phone: </label>
              <Input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Contact phone number"
                className="w-80"
                maxLength={FIELD_LENGTH_LIMITS.phone}
              />
              <FaAsterisk className="text-red-500 text-xxs ml-2" />
            </div>
            <div className="flex items-center">
              <label className="w-40">Short Name: </label>
              <Input
                type="text"
                value={shortName}
                onChange={handleShortNameChange}
                placeholder="e.g. B-USA-JS"
                className="w-80"
                maxLength={FIELD_LENGTH_LIMITS.shortName}
              />
              <FaAsterisk className="text-red-500 text-xxs ml-2" />
            </div>
          </div>

          <div className="mt-2 mb-3 text-xs text-gray-500 italic">
            <p>Note: </p>
            <p>
              Short Name is{" "}
              <strong className="font-bold">auto-generated</strong> with format:{" "}
              <strong className="font-bold">B/C-Country-Initials</strong>
            </p>
            <p>
              B = Business (with company), C = Client (without company).
              Initials are from the contact person.
            </p>
          </div>

          <div className="flex space-x-4 mt-4">
            <Button onClick={handleSubmit}>
              {action === "update" ? "Update" : "Create"}
            </Button>
            <Button
              onClick={hide}
              className="text-gray-500 hover:text-gray-800"
            >
              Cancel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const PackageDestination = () => {
  const { loading, error, data } = useQuery(FETCH_ALL_PACKAGE_ADDRESSES);
  const { present, hide } = useModals();
  const [searchTerm, setSearchTerm] = useState("");

  const addresses = data?.allPackageAddresses || [];

  const filteredAddresses = useMemo(() => {
    if (!searchTerm.trim()) return addresses;

    const term = searchTerm.toLowerCase().trim();
    return addresses.filter(
      (address) =>
        (address.shortName?.toLowerCase() || "").includes(term) ||
        (address.company?.toLowerCase() || "").includes(term) ||
        (address.attention?.toLowerCase() || "").includes(term) ||
        (address.email?.toLowerCase() || "").includes(term) ||
        (address.phone?.toLowerCase() || "").includes(term) ||
        (address.streetAddress?.toLowerCase() || "").includes(term) ||
        (address.streetAddress2?.toLowerCase() || "").includes(term) ||
        (address.cityPostal?.toLowerCase() || "").includes(term) ||
        (address.country?.toLowerCase() || "").includes(term) ||
        (
          getCountryDisplayName(
            address.country,
            address.countryName,
          )?.toLowerCase() || ""
        ).includes(term),
    );
  }, [addresses, searchTerm]);

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  return (
    <Page title="Package Destination Addresses" backTo="/tools/package">
      <div className="p-6">
        <div className="mb-4 flex items-center space-x-8">
          <Button
            data-testid="add-address-btn"
            onClick={() =>
              present({
                title: "Add Destination Address",
                center: true,
                children: (
                  <PackageAddressForm
                    hide={hide}
                    action="create"
                    existingAddresses={addresses}
                  />
                ),
                isBeingPresented: true,
              })
            }
          >
            + Add Address
          </Button>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 w-64">
              <BsSearch className="text-gray-400" />
              <Input
                type="text"
                placeholder="Search addresses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>

            <div className="text-sm text-gray-500">
              {filteredAddresses.length} address
              {filteredAddresses.length !== 1 ? "es" : ""} found
              {searchTerm && ` for "${searchTerm}"`}
            </div>
          </div>
        </div>

        <div className="card p-0 overflow-x-auto" data-testid="address-list">
          <table className="w-full table-fixed">
            <thead>
              <tr className="sticky top-0 bg-gray-200 dark:bg-gray-800 z-10">
                <th className="w-[8%] px-6 py-3 text-left">Actions</th>
                <th className="w-[10%] px-6 py-3 text-left">Name</th>
                <th className="w-[12%] px-6 py-3 text-left">Attention</th>
                <th className="w-[14%] px-6 py-3 text-left">Company</th>
                <th className="w-[12%] px-6 py-3 text-left">Phone</th>
                <th className="w-[18%] px-6 py-3 text-left">Email</th>
                <th className="w-[26%] px-6 py-3 text-left">Address</th>
              </tr>
            </thead>
            <tbody>
              {filteredAddresses.length > 0 ? (
                filteredAddresses.map((address) => (
                  <tr
                    key={address.id}
                    className="border-y border-gray-100 dark:border-gray-700  dark:hover:bg-gray-900"
                  >
                    <td className="px-6 py-3">
                      <div className="flex justify-start space-x-2">
                        <Button
                          data-testid="edit-address-btn"
                          onClick={() =>
                            present({
                              title: "Edit Destination Address",
                              center: true,
                              children: (
                                <PackageAddressForm
                                  hide={hide}
                                  address={address}
                                  action="update"
                                  existingAddresses={addresses}
                                />
                              ),
                              isBeingPresented: true,
                            })
                          }
                        >
                          <BsPencil className="text-base" />
                        </Button>
                        <Button
                          data-testid="delete-address-btn"
                          onClick={() =>
                            present({
                              title: "Delete Destination Address",
                              center: true,
                              children: (
                                <PackageAddressForm
                                  hide={hide}
                                  address={address}
                                  action="delete"
                                />
                              ),
                              isBeingPresented: true,
                            })
                          }
                          danger
                        >
                          <BsTrash className="text-base" />
                        </Button>
                      </div>
                    </td>
                    <td className="px-6 py-3 break-words">
                      {address.shortName}
                    </td>
                    <td className="px-6 py-3 break-words">
                      {address.attention}
                    </td>
                    <td className="px-6 py-3 break-words">{address.company}</td>
                    <td className="px-6 py-3 break-words">{address.phone}</td>
                    <td className="px-6 py-3 break-words">{address.email}</td>
                    <td className="px-6 py-3">
                      <div className="flex flex-col">
                        {address.streetAddress && (
                          <span className="whitespace-normal break-words">
                            {address.streetAddress}
                          </span>
                        )}
                        {address.streetAddress2 && (
                          <span className="whitespace-normal break-words">
                            {address.streetAddress2}
                          </span>
                        )}
                        {address.cityPostal && (
                          <span className="whitespace-normal break-words">
                            {address.cityPostal}
                          </span>
                        )}
                        {address.country && (
                          <span>
                            {getCountryDisplayName(
                              address.country,
                              address.countryName,
                            )}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    {searchTerm
                      ? "No matching addresses found. Try a different search term."
                      : "No destination addresses found. Add your first address."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Page>
  );
};

export default PackageDestination;
